import Fuse from "fuse.js";
import { GetState, SetState } from "zustand";

import type { FilterOptions, GridColumn, IAvailableSummarizeField } from "../../../../schemas/report";

import { selectQuestionTypeDisplayName } from "../../utils";

import { ISurveyQuestion } from "../../Components/QuestionGroupingModal/schema";
import Config from "../../Config";
import { ReportStore } from "../model";
import {
	fetchEmployeeFields,
	fetchGroupingSession,
	fetchNonUniqueSurveyQuestions,
	fetchSurveyReportCSV,
	fetchUniqueSurveyQuestions,
	fetchUniqueSurveys,
} from "../requests";

const surveyReportStore = (set: SetState<ReportStore>, get: GetState<ReportStore>) => ({
	uniqueSurveyQuestions: [],
	getUniqueSurveyQuestions: (search) => {
		const { uniqueSurveyQuestions, uniqueSurveyQuestionsMap, questionGroupingModal, uniqueSurveyQuestionsFuse } =
			get();
		const { showOnlySelectedQuestions, selectedQuestions, getSelectedQuestionInformation } = questionGroupingModal;

		// Get the type of the first selected question. We will only show questions of the same type.
		const selectedQuestionInformation = getSelectedQuestionInformation();

		// get an array of the selected questions. These will always show on top.
		const selectedQuestionsArray: ISurveyQuestion[] = selectedQuestions
			.map((id) => {
				return uniqueSurveyQuestionsMap[id];
			})
			.filter((value) => value !== null && value !== undefined);

		// get an array of all review form ids that are selected
		const selectedSurveyIDs = selectedQuestionsArray?.map((question) => question?.surveyID);

		// fuzzy search the questions iff we have a search term
		let questions: ISurveyQuestion[] =
			search && search.length > 0
				? (uniqueSurveyQuestionsFuse?.search(search).filter((question) => !!question) as ISurveyQuestion[])
				: uniqueSurveyQuestions;

		// we are only allowing numeric text and select (multi/single) types at this time
		// if there are no selected questions, we will filter out all questions that are not numeric or text
		// if there are selected questions, we will filter out all questions that are not the same type as the selected questions
		if (selectedQuestionsArray.length === 0) {
			const allowedQuestionTypes = [...Config.numericTypes, ...Config.textTypes, ...Config.selectTypes];
			questions = questions.filter((question) => allowedQuestionTypes.includes(question.questionType));
		} else if (
			selectedQuestionsArray[0]?.questionType &&
			Config.numericTypes.includes(selectedQuestionsArray[0].questionType)
		) {
			questions = questions.filter((question) => Config.numericTypes.includes(question.questionType));
		} else if (
			selectedQuestionsArray[0]?.questionType &&
			Config.textTypes.includes(selectedQuestionsArray[0].questionType)
		) {
			questions = questions.filter((question) => Config.textTypes.includes(question.questionType));
		} else if (Config.selectTypes.includes(selectedQuestionsArray[0].questionType)) {
			questions = questions.filter((question) => Config.selectTypes.includes(question.questionType));
		}

		// filter out questions from any selected review forms
		if (selectedQuestionInformation) {
			questions = questions.filter((question) => !selectedSurveyIDs.includes(question.surveyID));
		}

		if (showOnlySelectedQuestions) {
			questions = selectedQuestionsArray.filter((x) => !!x) as ISurveyQuestion[];
		} else {
			// add the selected questions to the top, remove selected questions from the searched list.
			questions = [
				...(selectedQuestionsArray.filter((x) => !!x) as ISurveyQuestion[]),
				...questions.filter((question) => !selectedQuestions.includes(question._id)),
			];
		}

		// the table expects the questions in a different format.
		// TODO - the table should just accept the questions directly
		const formattedQuestions = questions.map((item) => {
			return {
				id: item._id,
				question: item.question,
				// If its not a grouped question, then its a singleton with only
				// itself as part of the group.
				questionsInGroup: item.questionsInGroup || [item.questionHash],
				isGrouped: item.isGrouped || false,
				surveyName: item.surveyName || "Unknown",
				questionType: selectQuestionTypeDisplayName(item.questionType),
			};
		});

		return formattedQuestions;
	},
	fetchGroupedSurveyQuestions: async () => {
		const { client, report } = get();
		if (!client || !report) return;

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");
		const questionGroupingSession = await fetchGroupingSession(client, report.questionGroupingSessionID ?? "");
		const groupingSession = {
			reviewQueryID: questionGroupingSession?.reviewQueryID ?? null,
		};

		const groupedQuestions =
			questionGroupingSession?.selectedQuestions
				.filter((question) => {
					return question.isGrouped;
				})
				.map((question) => {
					return {
						id: question._id,
						label: question.name,
						questionsInGroup: question.questions,
						summaryFunction: question.summaryFunction,
					};
				}) ?? [];
		set({ groupedQuestions, groupingSession });
	},

	fetchSurveyQuestions: async () => {
		try {
			const { client, report } = get();

			if (!client) throw new Error("Client not initialized");
			if (!report) throw new Error("Report not initialized");

			// const filters = get().getAppliedFilters();

			const questionGroupingSessionID = report.questionGroupingSessionID;
			const uniqueSurveyQuestions =
				(await fetchUniqueSurveyQuestions(client, questionGroupingSessionID ?? "", [])) ?? [];

			// fetch questions relevant to each report, not specific to a review report
			const nonUniqueSurveyQuestions =
				(await fetchNonUniqueSurveyQuestions(client, questionGroupingSessionID ?? "", [])) ?? [];

			// index questions by _id
			const uniqueSurveyQuestionsMap = uniqueSurveyQuestions?.reduce((acc, question) => {
				acc[question._id] = question;
				return acc;
			}, {});

			const nonUniqueSurveyQuestionsMap = nonUniqueSurveyQuestions?.reduce((acc, question) => {
				acc[question._id] = question;
				return acc;
			}, {});

			// create a fuzzy search instance
			const uniqueSurveyQuestionsFuse = new Fuse(uniqueSurveyQuestions, {
				shouldSort: true,
				includeScore: false,
				threshold: 0.4,
				location: 0,
				distance: 1000,
				maxPatternLength: 32,
				minMatchCharLength: 1,
				keys: ["question", "questionType", "surveyName"],
			});

			const hasGroupableQuestions =
				uniqueSurveyQuestions.filter((question) =>
					[...Config.numericTypes, ...Config.textTypes, ...Config.selectTypes].includes(question.questionType)
				).length > 0;

			set({
				hasGroupableQuestions,
				uniqueSurveyQuestions,
				uniqueSurveyQuestionsMap,
				uniqueSurveyQuestionsFuse,
				nonUniqueSurveyQuestions,
				nonUniqueSurveyQuestionsMap,
			});
		} catch (error) {
			console.log(error);
		}
	},

	fetchAvailableSurveyColumns: async () => {
		const { client, columnMetadata, report, fetchSurveyQuestions, fetchGroupedSurveyQuestions } = get();

		if (!client) throw new Error("Client not initialized");

		const surveyFields = Config.surveyFields;
		const surveyFieldsAsColumns: GridColumn[] = Object.keys(surveyFields).map((field) => {
			const column: GridColumn = {
				field: `survey_${field}`,
				headerName: surveyFields[field].headerName,
				sortable: true,
				pinned: null,
				fieldType: "SURVEY_FIELD",
				fieldValueType: surveyFields[field].type,
				fieldValueTypeLabel: Config.goalTypeLabels[surveyFields[field].type],
				fieldPath: surveyFields[field].path,
				columnMetadata: columnMetadata[field],
			};

			// Only include valueGetter if field.valueGetter is defined
			if (surveyFields[field].valueGetter) {
				column.valueGetter = surveyFields[field].valueGetter;
			}

			return column;
		});

		await Promise.all([fetchSurveyQuestions(), fetchGroupedSurveyQuestions()]);

		// need to get() grouped questions after fetchGroupedQuestions runs
		const groupedQuestions = get().groupedQuestions;

		// return sorted questions
		const uniqueSurveyQuestionsAsColumns: GridColumn[] = get()
			.uniqueSurveyQuestions.map((question) => {
				return {
					field: `question_${question._id}`,
					headerName: question.question,
					sortable: true,
					pinned: null,
					fieldType: "SURVEY_QUESTION" as const,
					fieldValueType: question.questionType,
					fieldPath: ["questions", question._id, "answer"],
					columnMetadata: {
						...columnMetadata[`question_${question._id}`],
						min: question.min,
						max: question.max,
					},
				};
			})
			.sort((a, b) =>
				a.headerName.localeCompare(b.headerName, undefined, { numeric: true, sensitivity: "base" })
			);

		const nonUniqueSurveyQuestionsAsColumns: GridColumn[] = get().nonUniqueSurveyQuestions.map((question) => ({
			field: `question_${question._id}`,
			headerName: question.question,
			sortable: true,
			pinned: null,
			fieldType: "SURVEY_QUESTION",
			fieldValueType: question.questionType,
			fieldPath: ["questions", question._id, "answer"],
			columnMetadata: { ...columnMetadata[`question_${question._id}`], min: question.min, max: question.max },
		}));

		let groupedNonUniqueSurveyQuestionsAsColumns: GridColumn[] = [];
		if (groupedQuestions.length) {
			groupedNonUniqueSurveyQuestionsAsColumns = groupedQuestions
				.map((question) => {
					const firstNonUniqueQuestion = nonUniqueSurveyQuestionsAsColumns.find(
						(nonUniqueQuestion) => nonUniqueQuestion.field === `question_${question.questionsInGroup[0]}`
					);

					const fieldValueType = firstNonUniqueQuestion?.fieldValueType;
					const gridColumn: GridColumn = {
						field: `groupedquestion_${question.id}`,
						headerName: question.label,
						sortable: true,
						pinned: null,
						fieldType: "GROUPED_QUESTION",
						fieldValueType: fieldValueType,
						fieldPath: ["groupedQuestions", question.id, "answer"],
						columnMetadata: columnMetadata[`groupedquestion_${question._id}`],
					};

					return gridColumn;
				})
				.filter((column) => column.fieldValueType != null);
		}

		const employeeFields = await fetchEmployeeFields(client, report);
		const employeeFieldsAsColumns: GridColumn[] = employeeFields.map((field) => {
			return {
				field: `employee_${field.name}`,
				headerName: field.name,
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: field.type,
				fieldPath: ["participant", "custom_values", field.name],
				columnMetadata: columnMetadata[`employee_${field._id}`],
			};
		});

		const additionalEmployeeFieldsAsColumns: GridColumn[] = [
			{
				field: "employee_name",
				headerName: "Full Name",
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: "text",
				fieldPath: ["participant", "name"],
				columnMetadata: columnMetadata["employee_name"],
			},
			{
				field: `employee_manager_email`,
				headerName: "Manager's Email",
				sortable: true,
				pinned: null,
				fieldType: "EMPLOYEE_FIELD",
				fieldValueType: "text",
				fieldPath: ["participant", "manager", "email"],
				columnMetadata: columnMetadata[`employee_manager_email`],
			},
		];

		// combine and sort custom and additional employee fields
		const allSurveyFieldsAsColumns = [
			...employeeFieldsAsColumns,
			...additionalEmployeeFieldsAsColumns,
			...surveyFieldsAsColumns,
			...uniqueSurveyQuestionsAsColumns,
			...groupedNonUniqueSurveyQuestionsAsColumns,
		].sort((a, b) => {
			if (a.headerName < b.headerName) {
				return -1;
			}
			1;
			if (a.headerName > b.headerName) {
				return 1;
			}
			return 0;
		});

		return allSurveyFieldsAsColumns;
	},
	fetchSurveyFilterOptions: async (): Promise<FilterOptions> => {
		const { client } = get();

		if (!client) throw new Error("Client not initialized");

		const surveys = await fetchUniqueSurveys(client);
		const surveyNames = surveys.map((survey) => ({
			value: survey.name,
			label: survey.name,
		}));

		return {
			surveyName: surveyNames,
		};
	},
	fetchSurveyReportCSV: async () => {
		const { client, report, getEnabledGridColumns, sorts, isSummarizeRowsView } = get();

		if (!client) throw new Error("Client not initialized");
		if (!report) throw new Error("Report not initialized");

		const columns = getEnabledGridColumns().map((column) => {
			const type = column.fieldType;
			const columnID = column.field;

			return {
				type,
				id: columnID,
			};
		});

		const filters = get().getAppliedFilters();

		const summarizeBy = [];
		const summaryColumnMetadata = {};
		const summarizeByMetadata = {};
		const questionGroupingSessionID = report.questionGroupingSessionID;

		const csvJobID = await fetchSurveyReportCSV(
			client,
			columns,
			filters,
			sorts,
			report.name,
			questionGroupingSessionID || "",
			summarizeBy,
			[],
			isSummarizeRowsView,
			summaryColumnMetadata,
			summarizeByMetadata
		);

		set({ csvJobID });
	},
	getAvailableSurveySummarizeFields: (): IAvailableSummarizeField[] => [
		...Config.summarizeFields.employee,
		...Config.summarizeFields.survey,
	],
});

export default surveyReportStore;
